import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { CreationInvestorContext } from './creationSteps/CreationInvestorContext';
import WizardStepper from '../../core/components/wizardStepper/WizardStepper';
import InvestorDetailsStep from './creationSteps/InvestorDetailsStep';
import { isEmpty } from 'lodash';
import { CircularProgress } from '@material-ui/core';
import { useStyles } from '../users/creationsteps/StyleStep';
import AddRelatedFundsStep from './creationSteps/AddRelatedFundsStep';
import MatchingFieldsStep from './creationSteps/MatchingFieldsStep';
import { investorStatus } from './InvestorLookups';
import { getInitials } from '../../core/utils';
import FormTitleBar from '../../core/components/FormTitleBar';
import AddRelatedContactsStep from './creationSteps/addRelatedContacts/AddRelatedContactsStep';
import UserApi from '../../core/apis/UserApi';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import { GenerateMessageOnLeave } from '../../core/components/wizardStepper/GenerateMessageOnLeave';

export const STEP = {
  investorDetails: 'investorDetails',
  addRelatedFunds: 'addRelatedFunds',
  addRelatedContacts: 'addRelatedContacts',
  matchingFields: 'matchingFields'
};

const InvestorCreate = props => {
  const [investorContext, setInvestorContext] = useState({});
  const [currentAuthorizations, setCurrentAuthorizations] = useState([]);
  const [contactAuthorizationsIds, setContactAuthorizationsIds] = useState([]);
  const [contacts, setContacts] = useState([]);
  const authorizationsGridToDeleteRef = useRef([]);
  const { permissions = [] } = props;
  const classes = useStyles();
  const redirect = useRedirect();
  const [activeStep, setActiveStep] = useState(0);
  const [isDefaultListOfStepsSet, setIsDefaultListOfStepsSet] = useState(false);
  const [activeListOfSteps, setActiveListOfSteps] = useState([]);
  const [isBackDisabled, setIsBackDisabled] = useState(false);
  const [relatedFundsUploaded, setRelatedFundsUploaded] = useState([]);
  const providerFormContext = useMemo(
    () => ({ investorContext, setInvestorContext }),
    [investorContext, setInvestorContext]
  );
  const [isNextAvailable, setIsNextAvailable] = useState(false);
  const [isFirstStepPristine, setIsFirstStepPristine] = useState(true);
  const [isSaveAndCloseStep, setIsSaveAndCloseStep] = useState(false);
  const [permissionGroupsUploaded, setPermissionGroupsUploaded] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [permissionToDelete, setPermissionToDelete] = useState([]);
  const shouldQuitRef = useRef(false);
  const notifyServerError = useNotifyServerError();
  const [isSpinnerOnNextButton, setIsSpinnerOnNextButton] = useState(false);
  const notify = useNotify();

  const [
    hasEditInvestorPermission,
    hasWriteFundInvestorPermission,
    hasDeleteFundInvestorPermission,
    hasAssignUserAccessRights
  ] = useMemo(
    () => [
      permissions.includes('AUTH_EDIT_INVESTOR'),
      permissions.includes('AUTH_WRITE_FUND_INVESTOR'),
      permissions.includes('AUTH_DELETE_FUND_INVESTOR'),
      permissions.includes('AUTH_WRITE_USER_AUTHORIZATION')
    ],
    [permissions]
  );

  const isLastStep = useCallback(() => {
    return !!(
      !isEmpty(activeListOfSteps) && activeListOfSteps.length === activeStep + 1
    );
  }, [activeStep, activeListOfSteps]);

  const isItSaveAndCloseStep = useCallback(() => {
    if (!isEmpty(activeListOfSteps)) {
      return !!(
        (activeListOfSteps[activeStep].name === STEP.addRelatedFunds &&
          !isLastStep()) ||
        (activeListOfSteps[activeStep].name === STEP.addRelatedContacts &&
          !isLastStep())
      );
    }
  }, [activeListOfSteps, activeStep, isLastStep]);

  useEffect(() => {
    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.investorDetails &&
      investorContext.detailsFormState
    ) {
      const {
        valid: isDetailsFormValid,
        submitting: isDetailsFormSubmitting,
        pristine: isDetailsFormPristine
      } = investorContext.detailsFormState;

      setIsFirstStepPristine(isDetailsFormPristine);
      setIsNextAvailable(isDetailsFormValid && !isDetailsFormSubmitting);
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.addRelatedFunds &&
      investorContext.relatedFundsFormState
    ) {
      const {
        valid: isRelatedFundsFormValid,
        submitting: isRelatedFundFormSubmitting
      } = investorContext.relatedFundsFormState;

      setIsNextAvailable(
        isRelatedFundsFormValid && !isRelatedFundFormSubmitting
      );
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.addRelatedContacts
    ) {
      setIsNextAvailable(true);
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.matchingFields &&
      investorContext.matchingFieldFormState
    ) {
      setIsNextAvailable(!investorContext.matchingFieldFormState.submitting);
    }

    isItSaveAndCloseStep()
      ? setIsSaveAndCloseStep(true)
      : setIsSaveAndCloseStep(false);
  }, [investorContext, activeStep, isItSaveAndCloseStep, activeListOfSteps]);

  const handleClose = useCallback(() => {
    investorContext.investor
      ? redirect(`/investors/${investorContext.investor.id}`)
      : redirect('list', '/investors');
  }, [investorContext, redirect]);

  const setDefaultListOfSteps = useCallback(() => {
    const stepsByDefault = [
      {
        name: STEP.investorDetails,
        label: 'Investor Details',
        leavingPromptLabel: 'Investor Details',
        description:
          'Please fill in the mandatory fields to create new Investor. All other details, such as addresses, phone numbers, etc, you can add later.',
        component: InvestorDetailsStep,
        stepNo: 1,
        completed: false
      }
    ];

    if (hasWriteFundInvestorPermission) {
      stepsByDefault.push({
        name: STEP.addRelatedFunds,
        label: 'Add Related Funds',
        leavingPromptLabel: 'Related Funds',
        description:
          'Select one or multiple Funds from the list to add them to the Investor.',
        component: AddRelatedFundsStep,
        stepNo: 2,
        completed: false
      });
    }

    if (hasWriteFundInvestorPermission && hasAssignUserAccessRights) {
      stepsByDefault.push({
        name: STEP.addRelatedContacts,
        label: 'Add Related Contacts',
        leavingPromptLabel: 'Related Contacts',
        description:
          'Add one or more contacts to the investor from a list below.',
        component: AddRelatedContactsStep,
        stepNo: 3,
        completed: false
      });
    }

    if (hasEditInvestorPermission) {
      stepsByDefault.push({
        name: STEP.matchingFields,
        label: 'Matching Fields',
        leavingPromptLabel: 'Matching Fields',
        description: '',
        component: MatchingFieldsStep,
        stepNo: 4,
        completed: false
      });
    }

    return stepsByDefault;
  }, [
    hasWriteFundInvestorPermission,
    hasEditInvestorPermission,
    hasAssignUserAccessRights
  ]);

  useEffect(() => {
    if (isDefaultListOfStepsSet === false) {
      setActiveListOfSteps(setDefaultListOfSteps());
      setIsDefaultListOfStepsSet(true);
    }
  }, [
    setActiveListOfSteps,
    isDefaultListOfStepsSet,
    setIsDefaultListOfStepsSet,
    setDefaultListOfSteps
  ]);

  useEffect(() => {
    if (activeStep === 1 && !hasEditInvestorPermission) {
      setIsBackDisabled(true);
    } else {
      setIsBackDisabled(false);
    }
  }, [activeStep, hasEditInvestorPermission]);

  const getActiveStep = () => {
    const step = activeListOfSteps[activeStep];
    return (
      <step.component
        variant="standard"
        onSubmitEnd={onSubmitEnd}
        setIsNextAvailable={setIsNextAvailable}
        hasDeleteFundInvestorPermission={hasDeleteFundInvestorPermission}
        relatedFundsUploaded={relatedFundsUploaded}
        setRelatedFundsUploaded={setRelatedFundsUploaded}
        currentAuthorizations={currentAuthorizations}
        setCurrentAuthorizations={setCurrentAuthorizations}
        contactAuthorizationsIds={contactAuthorizationsIds}
        setContactAuthorizationsIds={setContactAuthorizationsIds}
        authorizationsGridToDeleteRef={authorizationsGridToDeleteRef}
        permissionToDelete={permissionToDelete}
        setPermissionToDelete={setPermissionToDelete}
        handleClose={handleClose}
        permissionGroupsUploaded={permissionGroupsUploaded}
        contacts={contacts}
        setContacts={setContacts}
        setIsSpinnerOnNextButton={setIsSpinnerOnNextButton}
        setPermissionGroupsUploaded={setPermissionGroupsUploaded}
        originalPermissions={originalPermissions}
        setActiveListOfSteps={setActiveListOfSteps}
        activeListOfSteps={activeListOfSteps}
        hasAssignUserAuthorization={hasAssignUserAccessRights}
      />
    );
  };

  const onSubmitEnd = () => {
    if (activeStep === activeListOfSteps.length - 1 || shouldQuitRef.current) {
      notify('ra.notification.created');
      handleClose();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBackForm = () => {
    setActiveStep(activeStep - 1);
  };

  function permissionSetter(array, perm) {
    return [
      ...array,
      {
        id: `${perm.userId}:${perm.investorId}:${perm.fundId}:${perm.groupId}`,
        ...perm
      }
    ];
  }

  const createPermissions = permissionsToCreate => {
    UserApi.createUserauthorizations(permissionsToCreate)
      .then(res => {
        res.forEach(item => {
          setPermissionGroupsUploaded(prev => permissionSetter(prev, item));
          setOriginalPermissions(prev => permissionSetter(prev, item));
        });
        setCurrentAuthorizations([]);
      })
      .catch(e => notifyServerError(e));
  };

  const handleNextForm = e => {
    const buttonClicked = e.currentTarget.getAttribute('buttonkey');
    if (buttonClicked === 'saveAndClose') {
      shouldQuitRef.current = true;
    }

    if (activeListOfSteps[activeStep].name === STEP.investorDetails) {
      document
        .getElementById('investorDetailsForm')
        .dispatchEvent(new Event('submit', { cancelable: true }));
    }

    if (activeListOfSteps[activeStep].name === STEP.addRelatedFunds) {
      document
        .getElementById('addRelatedFundsForm')
        .dispatchEvent(new Event('submit', { cancelable: true }));
    }

    if (activeListOfSteps[activeStep].name === STEP.addRelatedContacts) {
      if (currentAuthorizations.length > 0) {
        createPermissions(currentAuthorizations);
        const updatedListOfStep = activeListOfSteps.map(step => {
          if (step.name === STEP.addRelatedContacts) {
            return { ...step, completed: true };
          }
          return step;
        });
        setActiveListOfSteps(updatedListOfStep);
      }
      if (permissionToDelete.length > 0) {
        UserApi.deleteUserauthorizations(permissionToDelete)
          .then(() => {
            setPermissionToDelete([]);
          })
          .catch(error => notifyServerError(error));
      }
      shouldQuitRef.current ? handleClose() : onSubmitEnd();
    }

    if (activeListOfSteps[activeStep].name === STEP.matchingFields) {
      document
        .getElementById('matchingFieldsForm')
        .dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  // Display the information about the Investor created in the top left of the Wizard once we reach the step 2
  const displayInvestorCreatedInfo = () => {
    return (
      <FormTitleBar
        recordLabel={'Investor ID: ' + investorContext.investor.id}
        name={investorContext.investor.name}
        status={investorStatus
          .find(status => status.id === investorContext.investor.status)
          .name.toUpperCase()}
        statusLabel={'Status:'}
        showAvatar={true}
        avatarContent={getInitials(investorContext.investor.name)}
      />
    );
  };

  if (isEmpty(activeListOfSteps)) {
    return (
      <div className={classes.progress}>
        <CircularProgress />
      </div>
    );
  } else {
    return (
      <WizardStepper
        hasRadioButtons={false}
        handleClose={handleClose}
        getActiveStep={getActiveStep}
        handleBackForm={handleBackForm}
        handleNextForm={handleNextForm}
        resource={'Investor'}
        isBackDisabled={isBackDisabled}
        isNextAvailable={isNextAvailable}
        activeListOfSteps={activeListOfSteps}
        activeStep={activeStep}
        isSaveAndCloseStep={isSaveAndCloseStep}
        providerFormContext={providerFormContext}
        customContext={CreationInvestorContext}
        generateMessageOnLeave={
          <GenerateMessageOnLeave
            listOfSteps={activeListOfSteps}
            resource={'Investor'}
            resourceName={investorContext?.investor?.name}
          />
        }
        resourceCreated={investorContext.investor}
        displayResourceCreatedInfo={displayInvestorCreatedInfo}
        shouldDisplayResourceCreatedInfo={true}
        isFirstStepPristine={isFirstStepPristine}
        isSpinnerOnNextButton={isSpinnerOnNextButton}
      />
    );
  }
};

export default InvestorCreate;
